import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { HostedAgentArtifact } from "@/api/client/hosted-agents-artifacts"

import { BookmarkPlus } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { useHostedAgentArtifacts } from "../hooks/use-hosted-agent-artifacts"
import {
  ARTIFACT_KIND_LABEL_KEYS,
  artifactDate,
  artifactListErrorKey,
  isArtifactStoreMissing,
  latestPerFamily,
} from "../utils/agent-artifacts-utils"
import {
  AGENT_RAIL_HEADER_CLASS,
  AGENT_RAIL_LABEL_CLASS,
  AGENT_RAIL_META_CLASS,
  AGENT_RAIL_SECTION_CLASS,
} from "../utils/agents-utils"

const COLLAPSED_LIMIT = 5

type ArtifactCardProps = {
  artifact: HostedAgentArtifact
  onSelect: (artifact: HostedAgentArtifact) => void
}

/** Compact card: 13 px title, then one 11 px `kind · person · date` line. */
function ArtifactCard({ artifact, onSelect }: ArtifactCardProps) {
  const { t, i18n } = useTranslation()
  const meta = [
    t(ARTIFACT_KIND_LABEL_KEYS[artifact.kind]),
    artifact.created_by_name || t("agents.artifacts.unknown_author"),
    // The UI language, not the browser's, and the thread list's format (#3207).
    artifactDate(artifact.updated_at || artifact.created_at, i18n.resolvedLanguage ?? "en"),
  ]
    .filter(Boolean)
    .join(" · ")
  return (
    <button
      className="bg-card hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring flex flex-col gap-1 rounded-md border px-3 py-2.5 text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none"
      data-testid="agent-artifact-card"
      type="button"
      onClick={() => onSelect(artifact)}
    >
      <span className="flex min-w-0 items-baseline gap-1.5">
        <span className="truncate text-[13px] leading-snug font-semibold">{artifact.title}</span>
        <span className={cn(AGENT_RAIL_META_CLASS, "shrink-0")}>
          {t("agents.artifacts.version", { version: artifact.version })}
        </span>
      </span>
      <span className={cn(AGENT_RAIL_META_CLASS, "line-clamp-1")}>
        {meta}
        {!artifact.published ? ` · ${t("agents.artifacts.draft")}` : ""}
      </span>
    </button>
  )
}

type AgentArtifactsSectionProps = {
  agentSlug: string
  onSelect: (artifact: HostedAgentArtifact) => void
}

/**
 * "Artifacts" rail section on the agent landing page (B4): one card per
 * artifact family (latest version), compact by default with a "show all"
 * toggle. Everyone with access to the agent sees the same published list —
 * plus their own unpublished drafts. Same micro-label section style as the
 * rest of the rail; the family count sits on the right of the label.
 */
export function AgentArtifactsSection({ agentSlug, onSelect }: AgentArtifactsSectionProps) {
  const { t } = useTranslation()
  const artifactsQuery = useHostedAgentArtifacts(agentSlug)
  const [showAll, setShowAll] = useState(false)

  const families = useMemo(() => latestPerFamily(artifactsQuery.data ?? []), [artifactsQuery.data])
  const visible = showAll ? families : families.slice(0, COLLAPSED_LIMIT)
  const hiddenCount = families.length - visible.length

  let body: React.ReactNode
  if (artifactsQuery.isPending) {
    body = (
      <div className="flex flex-col gap-1.5">
        {Array.from({ length: 2 }, (_, index) => (
          <div key={index} className="skeleton-warm h-14 rounded-md" />
        ))}
      </div>
    )
  } else if (artifactsQuery.isError && isArtifactStoreMissing(artifactsQuery.error)) {
    // The store is simply not provisioned here (503 artifact_store_unavailable):
    // an environment fact, not a failed load — so no retry, and it reads like
    // the empty state rather than an alert.
    body = (
      <div className="flex flex-col items-center gap-2 px-1 py-3 text-center" role="status">
        {/* Same 10 px tile as the empty state below — #3164's copy landed on
            the pre-polish radius during the rebase. */}
        <div className="bg-muted flex size-10 items-center justify-center rounded-md">
          <BookmarkPlus aria-hidden="true" className="text-muted-foreground size-5" />
        </div>
        <p className="text-muted-foreground text-sm">
          {t(artifactListErrorKey(artifactsQuery.error))}
        </p>
        <p className={AGENT_RAIL_META_CLASS}>{t("agents.artifacts.unavailable_hint")}</p>
      </div>
    )
  } else if (artifactsQuery.isError) {
    body = (
      <div className="space-y-2 px-1 py-2" role="alert">
        <p className="text-muted-foreground text-sm">
          {t(artifactListErrorKey(artifactsQuery.error))}
        </p>
        <Button size="sm" variant="outline" onClick={() => void artifactsQuery.refetch()}>
          {t("agents.roster.retry")}
        </Button>
      </div>
    )
  } else if (families.length === 0) {
    body = (
      <div className="flex flex-col items-center gap-2 px-1 py-3 text-center">
        <div className="bg-muted flex size-10 items-center justify-center rounded-md">
          <BookmarkPlus aria-hidden="true" className="text-muted-foreground size-5" />
        </div>
        <p className="text-muted-foreground text-sm">{t("agents.artifacts.empty")}</p>
        <p className={AGENT_RAIL_META_CLASS}>{t("agents.artifacts.empty_hint")}</p>
      </div>
    )
  } else {
    body = (
      <div className="flex flex-col gap-1.5">
        {visible.map((artifact) => (
          <ArtifactCard key={artifact.id} artifact={artifact} onSelect={onSelect} />
        ))}
        {hiddenCount > 0 || showAll ? (
          <Button
            className="self-start"
            size="sm"
            variant="link"
            onClick={() => setShowAll((value) => !value)}
          >
            {showAll
              ? t("agents.artifacts.show_less")
              : t("agents.artifacts.show_all", { count: families.length })}
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <section
      aria-label={t("agents.artifacts.title")}
      className={AGENT_RAIL_SECTION_CLASS}
      data-testid="agent-artifacts-section"
    >
      <div className={AGENT_RAIL_HEADER_CLASS}>
        <span className={AGENT_RAIL_LABEL_CLASS}>{t("agents.artifacts.title")}</span>
        {families.length > 0 ? (
          <span className={AGENT_RAIL_META_CLASS}>{families.length}</span>
        ) : null}
      </div>
      {body}
    </section>
  )
}
